/**
 * Custom Calendar Component
 * Replaces FullCalendar with a fully controlled custom solution
 */

(function() {
'use strict';

class GDCMCalendar {
    constructor(element, options = {}) {
        this.element = element;
        this.options = {
            initialView: 'dayGridMonth',
            events: [],
            eventClick: null,
            dateClick: null,
            eventDidMount: null,
            headerToolbar: {
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,listWeek'
            },
            height: 'auto',
            aspectRatio: 1.8,
            firstDay: 1, // Monday
            ...options
        };
        
        this.currentView = this.options.initialView;
        this.currentDate = new Date();
        this.events = [];
        this.eventsCallback = null;
        
        this.init();
    }
    
    init() {
        // Preserve existing classes and add our base class
        if (!this.element.classList.contains('gdcm-custom-calendar')) {
            this.element.classList.add('gdcm-custom-calendar');
        }
        this.render();
        this.bindEvents();
        
        // Load events if a callback function or URL string is provided
        if (typeof this.options.events === 'function' || typeof this.options.events === 'string') {
            this.refetchEvents();
        } else if (Array.isArray(this.options.events)) {
            this.events = this.options.events;
            this.render();
        }
    }
    
    render() {
        this.element.innerHTML = `
            <div class="gdcm-calendar-header">
                ${this.renderToolbar()}
            </div>
            <div class="gdcm-calendar-body">
                ${this.renderView()}
            </div>
        `;
    }
    
    renderToolbar() {
        const { left, center, right } = this.options.headerToolbar;
        
        return `
            <div class="gdcm-calendar-toolbar">
                <div class="gdcm-toolbar-section gdcm-toolbar-left">
                    ${this.renderToolbarSection(left)}
                </div>
                <div class="gdcm-toolbar-section gdcm-toolbar-center">
                    ${this.renderToolbarSection(center)}
                </div>
                <div class="gdcm-toolbar-section gdcm-toolbar-right">
                    ${this.renderToolbarSection(right)}
                </div>
            </div>
        `;
    }
    
    renderToolbarSection(section) {
        const parts = section.split(',');
        return parts.map(part => {
            switch (part.trim()) {
                case 'prev':
                    return `<button class="gdcm-calendar-btn gdcm-prev-btn" data-action="prev">
                        <div class="tw-h-6 tw-w-6 tw-text-white">
                            <svg viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  class="tw-h-full tw-w-full"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M15 6l-6 6l6 6" /></svg>
                        </div>
                    </button>`;
                case 'next':
                    return `<button class="gdcm-calendar-btn gdcm-next-btn" data-action="next">
                        <div class="tw-h-6 tw-w-6 tw-text-white">
                            <svg viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  class="tw-h-full tw-w-full"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 6l6 6l-6 6" /></svg>
                        </div>
                    </button>`;
                case 'today':
                    return '<button class="gdcm-calendar-btn gdcm-today-btn" data-action="today">Today</button>';
                case 'title':
                    return `<h2 class="gdcm-calendar-title">${this.getTitle()}</h2>`;
                case 'dayGridMonth':
                    return `<button class="gdcm-calendar-btn gdcm-view-btn ${this.currentView === 'dayGridMonth' ? 'active' : ''}" data-view="dayGridMonth">Month</button>`;
                case 'timeGridWeek':
                    return `<button class="gdcm-calendar-btn gdcm-view-btn ${this.currentView === 'timeGridWeek' ? 'active' : ''}" data-view="timeGridWeek">Week</button>`;
                case 'listWeek':
                    return `<button class="gdcm-calendar-btn gdcm-view-btn ${this.currentView === 'listWeek' ? 'active' : ''}" data-view="listWeek">List</button>`;
                default:
                    return '';
            }
        }).join('');
    }
    
    renderView() {
        switch (this.currentView) {
            case 'dayGridMonth':
                return this.renderMonthView();
            case 'timeGridWeek':
                return this.renderWeekView();
            case 'listWeek':
                return this.renderListView();
            default:
                return this.renderMonthView();
        }
    }
    
    renderMonthView() {
        const startOfMonth = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1);
        const endOfMonth = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 0);
        
        // Get the first day of the week for the first day of month
        const startOfCalendar = new Date(startOfMonth);
        const dayOfWeek = startOfMonth.getDay();
        const daysBack = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday = 0
        startOfCalendar.setDate(startOfMonth.getDate() - daysBack);
        
        // Generate 6 weeks worth of days (42 days)
        const days = [];
        const currentDay = new Date(startOfCalendar);
        
        for (let i = 0; i < 42; i++) {
            days.push(new Date(currentDay));
            currentDay.setDate(currentDay.getDate() + 1);
        }
        
        const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        
        return `
            <div class="gdcm-calendar-month">
                <div class="gdcm-calendar-header-row">
                    ${weekdays.map(day => `<div class="gdcm-calendar-header-cell">${day}</div>`).join('')}
                </div>
                <div class="gdcm-calendar-grid">
                    ${days.map(day => this.renderDayCell(day)).join('')}
                </div>
            </div>
        `;
    }
    
    renderDayCell(date) {
        const isCurrentMonth = date.getMonth() === this.currentDate.getMonth();
        const isToday = this.isToday(date);
        const isPast = date < new Date().setHours(0, 0, 0, 0);
        const dayEvents = this.getEventsForDate(date);
        
        // Check for background events to apply their className to the cell
        const backgroundEvents = dayEvents.filter(e => e.display === 'background');
        const regularEvents = dayEvents.filter(e => e.display !== 'background');
        const backgroundClasses = backgroundEvents.map(e => e.className || '').join(' ');
        
        const classes = [
            'gdcm-calendar-day',
            isCurrentMonth ? 'gdcm-current-month' : 'gdcm-other-month',
            isToday ? 'gdcm-today' : '',
            isPast ? 'gdcm-past' : 'gdcm-future',
            backgroundClasses
        ].filter(Boolean).join(' ');
        
        return `
            <div class="${classes}" data-date="${this.formatDate(date)}">
                <div class="gdcm-day-number">${date.getDate()}</div>
                <div class="gdcm-day-events">
                    ${regularEvents.map(event => this.renderEvent(event)).join('')}
                </div>
            </div>
        `;
    }
    
    renderWeekView() {
        const startOfWeek = this.getStartOfWeek(this.currentDate);
        const days = [];
        
        for (let i = 0; i < 7; i++) {
            const day = new Date(startOfWeek);
            day.setDate(startOfWeek.getDate() + i);
            days.push(day);
        }
        
        const hours = Array.from({ length: 24 }, (_, i) => i);
        
        return `
            <div class="gdcm-calendar-week">
                <div class="gdcm-week-header">
                    <div class="gdcm-time-column"></div>
                    ${days.map(day => `
                        <div class="gdcm-week-day-header ${this.isToday(day) ? 'gdcm-today' : ''}">
                            <div class="gdcm-week-day-name">${day.toLocaleDateString('en', { weekday: 'short' })}</div>
                            <div class="gdcm-week-day-number">${day.getDate()}</div>
                        </div>
                    `).join('')}
                </div>
                <div class="gdcm-week-body">
                    <div class="gdcm-time-column">
                        ${hours.map(hour => `
                            <div class="gdcm-time-slot">${hour.toString().padStart(2, '0')}:00</div>
                        `).join('')}
                    </div>
                    ${days.map(day => {
                        const isPastDay = day < new Date().setHours(0, 0, 0, 0);
                        return `
                        <div class="gdcm-week-day-column ${isPastDay ? 'gdcm-past-day' : ''}" data-date="${this.formatDate(day)}">
                            ${hours.map(hour => {
                                const hourDate = new Date(day);
                                hourDate.setHours(hour, 0, 0, 0);
                                const isPastHour = hourDate < new Date();
                                return `
                                <div class="gdcm-hour-slot ${isPastHour ? 'gdcm-past-hour' : ''}" data-hour="${hour}">
                                    ${this.getEventsForDateTime(day, hour).map(event => this.renderEvent(event)).join('')}
                                </div>
                                `;
                            }).join('')}
                        </div>
                        `;
                    }).join('')}
                </div>
            </div>
        `;
    }
    
    renderListView() {
        // Show events for the entire month, not just the current week
        const startOfMonth = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1);
        const endOfMonth = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 0);
        
        const monthEvents = this.events.filter(event => {
            const eventDate = new Date(event.start);
            return eventDate >= startOfMonth && eventDate <= endOfMonth;
        });
        
        // Group events by date
        const eventsByDate = {};
        monthEvents.forEach(event => {
            const dateKey = this.formatDate(new Date(event.start));
            if (!eventsByDate[dateKey]) {
                eventsByDate[dateKey] = [];
            }
            eventsByDate[dateKey].push(event);
        });
        
        // Sort dates
        const sortedDates = Object.keys(eventsByDate).sort();
        
        return `
            <div class="gdcm-calendar-list">
                ${sortedDates.map(date => `
                    <div class="gdcm-list-day">
                        <div class="gdcm-list-day-header">
                            <h3>${new Date(date).toLocaleDateString('en', { weekday: 'long', month: 'long', day: 'numeric' })}</h3>
                        </div>
                        <div class="gdcm-list-events">
                            ${eventsByDate[date].map(event => this.renderListEvent(event)).join('')}
                        </div>
                    </div>
                `).join('')}
                ${sortedDates.length === 0 ? '<div class="gdcm-no-events">No events this month</div>' : ''}
            </div>
        `;
    }
    
    renderEvent(event) {
        // Skip background events - they should only affect day cell styling
        if (event.display === 'background') {
            return '';
        }
        
        const eventType = event.extendedProps?.type || 'class';
        const eventDate = new Date(event.start);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const isPastEvent = eventDate < today;
        
        const classes = [
            'gdcm-calendar-event',
            `gdcm-event-${eventType}`,
            isPastEvent ? 'gdcm-event-past' : ''
        ].filter(Boolean).join(' ');
        
        return `
            <div class="${classes}" data-event-id="${event.id}" title="${event.title || ''}">
                <div class="gdcm-event-content">
                    <div class="gdcm-event-title">${event.title || ''}</div>
                    ${event.start ? `<div class="gdcm-event-time">${this.formatTime(new Date(event.start))}</div>` : ''}
                </div>
            </div>
        `;
    }
    
    renderListEvent(event) {
        const eventType = event.extendedProps?.type || 'class';
        const eventDate = new Date(event.start);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const isPastEvent = eventDate < today;
        
        const classes = [
            'gdcm-list-event',
            `gdcm-event-${eventType}`,
            isPastEvent ? 'gdcm-event-past' : ''
        ].filter(Boolean).join(' ');
        
        return `
            <div class="${classes}" data-event-id="${event.id}">
                <div class="gdcm-list-event-time">
                    ${this.formatTime(new Date(event.start))}
                </div>
                <div class="gdcm-list-event-content">
                    <div class="gdcm-list-event-title">${event.title}</div>
                    ${event.extendedProps?.description ? `<div class="gdcm-list-event-description">${event.extendedProps.description}</div>` : ''}
                </div>
            </div>
        `;
    }
    
    bindEvents() {
        // Toolbar button clicks
        this.element.addEventListener('click', (e) => {
            // Check if clicked element or its parent has data-action
            const actionButton = e.target.closest('[data-action]');
            if (actionButton) {
                const action = actionButton.dataset.action;
                this.handleAction(action);
            }
            
            // Check if clicked element or its parent has data-view
            const viewButton = e.target.closest('[data-view]');
            if (viewButton) {
                const view = viewButton.dataset.view;
                this.changeView(view);
            }
            
            if (e.target.matches('.gdcm-calendar-day')) {
                const dateStr = e.target.dataset.date;
                if (dateStr && this.options.dateClick) {
                    const clickedDate = new Date(dateStr);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    
                    // Prevent clicks on past dates
                    if (clickedDate >= today) {
                        this.options.dateClick({ date: clickedDate });
                    }
                }
            }
            
            // Handle hour slot clicks in week view
            if (e.target.matches('.gdcm-hour-slot')) {
                const hourSlot = e.target;
                const dayColumn = hourSlot.closest('.gdcm-week-day-column');
                const dateStr = dayColumn?.dataset.date;
                const hour = parseInt(hourSlot.dataset.hour);
                
                if (dateStr && this.options.dateClick) {
                    const clickedDate = new Date(dateStr);
                    clickedDate.setHours(hour, 0, 0, 0);
                    const now = new Date();
                    
                    // Prevent clicks on past hour slots
                    if (clickedDate >= now) {
                        this.options.dateClick({ date: clickedDate });
                    }
                }
            }
            
            if (e.target.closest('.gdcm-calendar-event, .gdcm-list-event')) {
                const eventEl = e.target.closest('.gdcm-calendar-event, .gdcm-list-event');
                const eventId = eventEl.dataset.eventId;
                const event = this.events.find(e => e.id == eventId);
                if (event && this.options.eventClick) {
                    const eventDate = new Date(event.start);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    
                    // Allow event clicks for today and future events
                    if (eventDate >= today) {
                        this.options.eventClick({ event });
                    }
                }
            }
        });
    }
    
    handleAction(action) {
        switch (action) {
            case 'prev':
                this.prev();
                break;
            case 'next':
                this.next();
                break;
            case 'today':
                this.today();
                break;
        }
    }
    
    prev() {
        switch (this.currentView) {
            case 'dayGridMonth':
                this.currentDate.setMonth(this.currentDate.getMonth() - 1);
                break;
            case 'timeGridWeek':
            case 'listWeek':
                this.currentDate.setDate(this.currentDate.getDate() - 7);
                break;
        }
        this.render();
        if (typeof this.options.events === 'function' || typeof this.options.events === 'string') {
            this.refetchEvents();
        }
    }
    
    next() {
        switch (this.currentView) {
            case 'dayGridMonth':
                this.currentDate.setMonth(this.currentDate.getMonth() + 1);
                break;
            case 'timeGridWeek':
            case 'listWeek':
                this.currentDate.setDate(this.currentDate.getDate() + 7);
                break;
        }
        this.render();
        if (typeof this.options.events === 'function' || typeof this.options.events === 'string') {
            this.refetchEvents();
        }
    }
    
    today() {
        this.currentDate = new Date();
        this.render();
        if (typeof this.options.events === 'function' || typeof this.options.events === 'string') {
            this.refetchEvents();
        }
    }
    
    changeView(view) {
        this.currentView = view;
        this.render();
        if (typeof this.options.events === 'function' || typeof this.options.events === 'string') {
            this.refetchEvents();
        }
    }
    
    // Event management
    addEvent(event) {
        this.events.push(event);
        this.render();
    }
    
    removeEvent(eventId) {
        this.events = this.events.filter(e => e.id !== eventId);
        this.render();
    }
    
    refetchEvents() {
        if (typeof this.options.events === 'function') {
            this.options.events(
                { start: this.getViewStart(), end: this.getViewEnd() },
                (events) => {
                    this.events = events;
                    this.render();
                    this.applyEventDidMount();
                },
                (error) => {
                    console.error('Failed to load events:', error);
                }
            );
        } else if (typeof this.options.events === 'string') {
            // Handle URL string
            fetch(this.options.events)
                .then(response => {
                    if (!response.ok) {
                        throw new Error('Network response was not ok');
                    }
                    return response.json();
                })
                .then(events => {
                    this.events = events || [];
                    this.render();
                    this.applyEventDidMount();
                })
                .catch(error => {
                    console.error('Failed to load events from URL:', error);
                });
        }
    }
    
    applyEventDidMount() {
        if (this.options.eventDidMount) {
            this.element.querySelectorAll('.gdcm-calendar-event, .gdcm-list-event').forEach(el => {
                const eventId = el.dataset.eventId;
                const event = this.events.find(e => e.id == eventId);
                if (event) {
                    this.options.eventDidMount({ event, el });
                }
            });
        }
    }
    
    // Utility methods
    getTitle() {
        switch (this.currentView) {
            case 'dayGridMonth':
            case 'listWeek':
                // Both month view and list view show monthly data
                return this.currentDate.toLocaleDateString('en', { month: 'long', year: 'numeric' });
            case 'timeGridWeek':
                const startOfWeek = this.getStartOfWeek(this.currentDate);
                const endOfWeek = new Date(startOfWeek);
                endOfWeek.setDate(startOfWeek.getDate() + 6);
                return `${startOfWeek.toLocaleDateString('en', { month: 'short', day: 'numeric' })} - ${endOfWeek.toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' })}`;
            default:
                return '';
        }
    }
    
    getStartOfWeek(date) {
        const start = new Date(date);
        const day = start.getDay();
        const diff = start.getDate() - day + (day === 0 ? -6 : 1); // Monday = 1
        start.setDate(diff);
        start.setHours(0, 0, 0, 0);
        return start;
    }
    
    isToday(date) {
        const today = new Date();
        return date.toDateString() === today.toDateString();
    }
    
    formatDate(date) {
        // Use local timezone instead of UTC to avoid day shifting
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
    
    formatTime(date) {
        return date.toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' });
    }
    
    getEventsForDate(date) {
        const dateStr = this.formatDate(date);
        return this.events.filter(event => {
            const eventDate = this.formatDate(new Date(event.start));
            return eventDate === dateStr;
        });
    }
    
    getEventsForDateTime(date, hour) {
        const dateStr = this.formatDate(date);
        return this.events.filter(event => {
            const eventStart = new Date(event.start);
            const eventEnd = new Date(event.end);
            
            // Check if event is on the same date
            if (this.formatDate(eventStart) !== dateStr) {
                return false;
            }
            
            // Check if event is active during this hour
            const startHour = eventStart.getHours();
            const endHour = eventEnd.getHours();
            
            // Event is active if it starts at or before this hour and ends after this hour
            // OR if it starts during this hour
            return (startHour <= hour && endHour > hour) || startHour === hour;
        });
    }
    
    getViewStart() {
        switch (this.currentView) {
            case 'dayGridMonth':
                return new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1);
            case 'timeGridWeek':
                return this.getStartOfWeek(this.currentDate);
            case 'listWeek':
                // List view shows monthly events, so return start of month
                return new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1);
            default:
                return new Date();
        }
    }
    
    getViewEnd() {
        switch (this.currentView) {
            case 'dayGridMonth':
                return new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 0);
            case 'timeGridWeek':
                const weekEnd = this.getStartOfWeek(this.currentDate);
                weekEnd.setDate(weekEnd.getDate() + 6);
                return weekEnd;
            case 'listWeek':
                // List view shows monthly events, so return end of month
                return new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 0);
            default:
                return new Date();
        }
    }
    
    // Public API to match FullCalendar
    render() {
        this.element.innerHTML = `
            <div class="gdcm-calendar-header">
                ${this.renderToolbar()}
            </div>
            <div class="gdcm-calendar-body">
                ${this.renderView()}
            </div>
        `;
        this.applyEventDidMount();
    }
}

// Make available globally for compatibility
if (typeof window !== 'undefined') {
    window.GDCMCalendar = GDCMCalendar;
}

})(); // End IIFE
